import React, { useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";

const ACAudit = ({ onBack }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [view, setView] = useState("list"); // 'form' or 'list'
    const [audits, setAudits] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedAudit, setSelectedAudit] = useState(null);

    // Advanced Management State
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [filterRisk, setFilterRisk] = useState("All");
    const [filterDate, setFilterDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);

    const initialFormState = {
        // Customer Details
        customerName: "",
        mobileNumber: "",
        address: "",
        location: "Sriperumbudur",
        // AC Details
        ACType: "Split",
        serialNumber: "",
        model: "",
        tonnage: "",
        installationYear: new Date().getFullYear(),
        roomSize: "",
        usageHoursPerDay: "",
        // Performance Check
        coolingPerformance: "Good",
        gasPressure: "",
        compressorCondition: "Good",
        condenserCondition: "Good",
        evaporatorCondition: "Good",
        airflowCondition: "Good",
        noiseLevel: "Low",
        waterLeakage: false,
        electricalCondition: "Good",
        thermostatAccuracy: "Accurate",
        powerConsumption: "",
        // Maintenance Check
        filterStatus: "Clean",
        coilCleaningRequired: false,
        gasRefillingRequired: false,
        capacitorStatus: "Good",
        wiringStatus: "Good",
        // Recommendation
        recommendedActions: "",
        estimatedServiceCost: "",
        technicianName: "",
        auditDate: new Date().toISOString().split('T')[0],
        nextServiceDue: "",
        grillTemperature: "",
    };

    const [formData, setFormData] = useState(initialFormState);
    const [healthScore, setHealthScore] = useState(100);
    const [riskLevel, setRiskLevel] = useState("Low Risk");

    const [technicians, setTechnicians] = useState([]);

    // Fetch Technicians
    const fetchTechnicians = async () => {
        try {
            const snap = await getDocs(collection(db, "EMPLOYEES"));
            const data = snap.docs.map(doc => doc.data().name).filter(Boolean);
            setTechnicians(data.sort());
        } catch (error) {
            console.error("Error fetching technicians:", error);
        }
    };

    useEffect(() => {
        fetchTechnicians();
    }, []);

    // Fetch Audits
    const fetchAudits = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "AC_AUDITS"), orderBy("createdAt", "desc"));
            const snap = await getDocs(q);
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAudits(data);
        } catch (error) {
            console.error("Error fetching audits:", error);
            toast.error("Failed to load audit history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (view === "list") fetchAudits();
    }, [view]);

    // Calculate Health Score
    useEffect(() => {
        let score = 100;

        if (formData.coolingPerformance === "Poor") score -= 20;
        if (formData.gasRefillingRequired) score -= 15;
        if (formData.filterStatus === "Dirty") score -= 10;
        if (formData.waterLeakage) score -= 15;
        if (formData.compressorCondition === "Critical") score -= 25;

        setHealthScore(score);

        if (score >= 80) setRiskLevel("Low Risk");
        else if (score >= 60) setRiskLevel("Medium Risk");
        else setRiskLevel("High Risk");
    }, [formData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.customerName || !formData.mobileNumber) {
            toast.error("Please fill required customer details");
            return;
        }

        setLoading(true);
        try {
            const auditData = {
                ...formData,
                healthScore,
                riskLevel,
                installationYear: Number(formData.installationYear),
                usageHoursPerDay: Number(formData.usageHoursPerDay),
                gasPressure: Number(formData.gasPressure),
                powerConsumption: Number(formData.powerConsumption),
                grillTemperature: Number(formData.grillTemperature),
                estimatedServiceCost: Number(formData.estimatedServiceCost),
                auditDate: new Date(formData.auditDate),
                nextServiceDue: formData.nextServiceDue ? new Date(formData.nextServiceDue) : null,
                updatedAt: serverTimestamp(),
            };

            if (isEditing) {
                await updateDoc(doc(db, "AC_AUDITS", editId), auditData);
                toast.success("Audit Updated Successfully!");
            } else {
                auditData.createdAt = serverTimestamp();
                await addDoc(collection(db, "AC_AUDITS"), auditData);
                toast.success("AC Audit Report Saved Successfully!");
            }
            setFormData(initialFormState);
            setIsEditing(false);
            setEditId(null);
            setView("list");
        } catch (error) {
            console.error("Error saving audit:", error);
            toast.error("Failed to save audit report");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (audit) => {
        const dateStr = audit.auditDate?.seconds
            ? new Date(audit.auditDate.seconds * 1000).toISOString().split('T')[0]
            : audit.auditDate ? new Date(audit.auditDate).toISOString().split('T')[0] : "";

        const nextDateStr = audit.nextServiceDue?.seconds
            ? new Date(audit.nextServiceDue.seconds * 1000).toISOString().split('T')[0]
            : audit.nextServiceDue ? new Date(audit.nextServiceDue).toISOString().split('T')[0] : "";

        setFormData({
            ...audit,
            auditDate: dateStr,
            nextServiceDue: nextDateStr
        });
        setIsEditing(true);
        setEditId(audit.id);
        setView("form");
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this audit record?")) {
            setLoading(true);
            try {
                await deleteDoc(doc(db, "AC_AUDITS", id));
                toast.success("Audit Deleted Successfully");
                fetchAudits();
            } catch (error) {
                console.error("Error deleting audit:", error);
                toast.error("Failed to delete audit");
            } finally {
                setLoading(false);
            }
        }
    };

    const exportToExcel = () => {
        const excelData = filteredAudits.map(a => ({
            "Audit ID": a.id,
            "Customer Name": a.customerName,
            "Mobile": a.mobileNumber,
            "Location": a.location,
            "AC Type": a.ACType,
            "Serial Number": a.serialNumber,
            "Health Score": a.healthScore,
            "Risk Level": a.riskLevel,
            "Grill Temp": a.grillTemperature,
            "Audit Date": new Date(a.auditDate.seconds ? a.auditDate.seconds * 1000 : a.auditDate).toLocaleDateString(),
            "Next Service": a.nextServiceDue ? new Date(a.nextServiceDue.seconds ? a.nextServiceDue.seconds * 1000 : a.nextServiceDue).toLocaleDateString() : "N/A",
            "Technician": a.technicianName
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "AC Audits");
        XLSX.writeFile(wb, `AC_Audit_History_${new Date().toLocaleDateString()}.xlsx`);
    };


    const SummaryPreview = ({ data, onClose }) => {
        const auditDate = data.auditDate?.seconds
            ? new Date(data.auditDate.seconds * 1000)
            : data.auditDate ? new Date(data.auditDate) : new Date();

        return (
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
                <style>
                    {`
                        @media print {
                            body * { visibility: hidden !important; }
                            #report-print, #report-print * { visibility: visible !important; }
                            #report-print { 
                                position: absolute !important; 
                                left: 0 !important; 
                                top: 0 !important; 
                                width: 100% !important;
                                padding: 0 !important;
                                margin: 0 !important;
                            }
                            @page { 
                                size: A4 portrait;
                                margin: 2mm; 
                            }
                            .no-print { display: none !important; }
                            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                            
                            /* Fix for scrollbar and content clipping */
                            .table-responsive { overflow: visible !important; }
                            .modal-body { overflow: visible !important; height: auto !important; max-height: none !important; }
                            .report-card { border: none !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
                            html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
                            
                            /* Compact everything for print */
                            .details-section { margin-bottom: 8px !important; }
                            .audit-table { margin-bottom: 0 !important; }
                            .audit-table tr { page-break-inside: avoid; }
                            
                            /* Ensure score box renders in print */
                            div[style*="borderRadius: 50px"] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color: black !important; }
                            div[style*="borderRadius: 50px"] * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; visibility: visible !important; color: black !important; }
                        }
                        .audit-table th { background-color: #f8f9fa; width: 35%; color: #6c757d; font-weight: 600; font-size: 0.65rem; padding: 4px 6px !important; }
                        .audit-table td { color: #212529; font-weight: 700; font-size: 0.78rem; padding: 4px 6px !important; }
                        .audit-table { margin-bottom: 8px !important; border-collapse: collapse !important; }
                        .report-card { border-radius: 20px; overflow: hidden; }
                        .section-title { font-size: 0.7rem; letter-spacing: 1px; }
                        .details-section { margin-bottom: 8px; }
                        .score-box { 
                            display: inline-block; 
                            padding: 8px 25px; 
                            border-radius: 50px; 
                            min-width: 200px;
                            text-align: center;
                        }
                    `}
                </style>
                <div className="modal-dialog modal-lg modal-dialog-centered" style={{maxHeight: '100vh', overflow: 'auto'}}>
                    <div className="modal-content border-0 shadow-lg report-card" id="report-print">
                        {/* Header Banner - Only Screen */}
                        <div className="bg-primary p-3 text-white d-flex justify-content-between align-items-center no-print" data-html2canvas-ignore="true">
                            <h6 className="mb-0 fw-bold">AC Health Audit Certificate</h6>
                            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                        </div>

                        <div className="modal-body p-2 pt-3" style={{maxHeight: 'none', overflow: 'visible'}}>
                            {/* Company Branding - Compact */}
                            <div className="text-center mb-2 border-bottom pb-2">
                                <h3 className="fw-black text-primary mb-0" style={{ letterSpacing: "-1px", fontSize: '1.2rem' }}>Asian Climate Controls</h3>
                                <p className="text-secondary x-small text-uppercase fw-bold mb-0" style={{ letterSpacing: "1.5px", fontSize: '0.6rem' }}>Performance Audit Certificate</p>
                            </div>

                            <div className="row g-1 mb-2">
                                <div className="col-12 text-center">
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '200px',
                                        height: '200px',
                                        borderRadius: '50%',
                                        background: `conic-gradient(${(data.healthScore || 0) < 60 ? '#ef4444' : (data.healthScore || 0) < 80 ? '#f97316' : '#22c55e'} 0deg ${(data.healthScore || 0) * 3.6}deg, #e5e7eb ${(data.healthScore || 0) * 3.6}deg 360deg)`,
                                        padding: '8px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        margin: '0 auto'
                                    }}>
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            background: '#ffffff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}>
                                            <div style={{
                                                fontSize: '3.5rem',
                                                fontWeight: '900',
                                                color: (data.healthScore || 0) < 60 ? '#ef4444' : (data.healthScore || 0) < 80 ? '#f97316' : '#22c55e',
                                                lineHeight: '1',
                                                letterSpacing: '-1px'
                                            }}>
                                                {Math.round(data.healthScore || 0)}
                                            </div>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                color: '#6b7280',
                                                marginTop: '4px',
                                                letterSpacing: '0.5px'
                                            }}>
                                                / 100
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        marginTop: '16px',
                                        fontSize: '0.95rem',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1.5px',
                                        color: (data.healthScore || 0) < 60 ? '#ef4444' : (data.healthScore || 0) < 80 ? '#f97316' : '#22c55e'
                                    }}>
                                        {data.riskLevel || 'Pending'}
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <div className="row gx-2 gy-0">
                                    {/* Details Grid */}
                                    <div className="col-md-6">
                                        <table className="table table-bordered audit-table mb-0">
                                            <tbody>
                                                <tr><th colSpan="2" className="bg-primary text-white text-center py-1 section-title shadow-sm">CUSTOMER DETAILS</th></tr>
                                                <tr><th>Name</th><td>{data.customerName}</td></tr>
                                                <tr><th>Mobile</th><td>{data.mobileNumber}</td></tr>
                                                <tr><th>Location</th><td>{data.location}</td></tr>
                                                <tr><th>Address</th><td>{data.address || "N/A"}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="col-md-6">
                                        <table className="table table-bordered audit-table mb-0">
                                            <tbody>
                                                <tr><th colSpan="2" className="bg-primary text-white text-center py-1 section-title shadow-sm">UNIT SPECIFICATIONS</th></tr>
                                                <tr><th>AC Type</th><td>{data.ACType}</td></tr>
                                                <tr><th>Serial Number</th><td>{data.serialNumber || data.brand}</td></tr>
                                                <tr><th>Model</th><td>{data.model || "N/A"}</td></tr>
                                                <tr><th>Capacity</th><td>{data.tonnage || "N/A"}</td></tr>
                                                <tr><th>Install Year</th><td>{data.installationYear}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Technical Metrics Table */}
                            <div className="table-responsive mb-2 mt-1">
                                <table className="table table-bordered audit-table mb-0 text-center">
                                    <thead>
                                        <tr><th colSpan="4" className="bg-dark text-white text-center py-1 section-title">TECHNICAL PERFORMANCE AUDIT</th></tr>
                                        <tr className="bg-light">
                                            <th className="py-1" style={{fontSize: '0.65rem'}}>PARAMETER</th>
                                            <th className="py-1" style={{fontSize: '0.65rem'}}>STATUS / VALUE</th>
                                            <th className="py-1" style={{fontSize: '0.65rem'}}>PARAMETER</th>
                                            <th className="py-1" style={{fontSize: '0.65rem'}}>STATUS / VALUE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th>Cooling Eff.</th><td>{data.coolingPerformance}</td>
                                            <th>Gas Pressure</th><td>{data.gasPressure} PSI</td>
                                        </tr>
                                        <tr>
                                            <th>Grill Temp.</th><td>{data.grillTemperature} °C</td>
                                            <th>Power Load</th><td>{data.powerConsumption} Amps</td>
                                        </tr>
                                        <tr>
                                            <th>Compressor</th><td>{data.compressorCondition}</td>
                                            <th>Noise Level</th><td>{data.noiseLevel}</td>
                                        </tr>
                                        <tr>
                                            <th>Water leakage</th>
                                            <td className={data.waterLeakage ? 'text-danger' : 'text-success'}>
                                                {data.waterLeakage ? 'Detected' : 'None'}
                                            </td>
                                            <th>Filter Status</th><td>{data.filterStatus}</td>
                                        </tr>
                                        <tr>
                                            <th>Coil Clean</th>
                                            <td colSpan="3" className={data.coilCleaningRequired ? 'text-warning' : 'text-success'}>
                                                {data.coilCleaningRequired ? 'Required' : 'Satisfactory'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="p-2 border border-primary rounded-2 bg-light mb-2">
                                <h6 className="fw-black text-primary text-uppercase x-small mb-1" style={{ letterSpacing: "0.5px", fontSize: '0.6rem' }}>Professional Recommendation</h6>
                                <p className="mb-0 text-dark" style={{ lineHeight: "1.3", fontSize: '0.75rem' }}>
                                    {data.recommendedActions || "Routine scheduled maintenance is advised to maintain peak performance."}
                                </p>
                            </div>

                            <div className="row g-1 text-center mb-2">
                                <div className="col-4">
                                    <div className="x-small text-muted text-uppercase mb-0" style={{ fontSize: '0.55rem' }}>Average Service Cost</div>
                                    <div className="fw-bold" style={{fontSize: '0.85rem'}}>₹ {data.estimatedServiceCost || 0}</div>
                                </div>
                                <div className="col-4 border-start border-end">
                                    <div className="x-small text-muted text-uppercase mb-0" style={{ fontSize: '0.55rem' }}>Audit Date</div>
                                    <div className="fw-bold" style={{fontSize: '0.85rem'}}>{auditDate.toLocaleDateString()}</div>
                                </div>
                                <div className="col-4">
                                    <div className="x-small text-muted text-uppercase mb-0" style={{ fontSize: '0.55rem' }}>Engineer</div>
                                    <div className="fw-bold" style={{fontSize: '0.85rem'}}>{data.technicianName}</div>
                                </div>
                            </div>

                            <div className="mt-2 pt-1 text-center border-top">
                                <p className="mb-0 text-muted x-small" style={{ fontSize: '0.6rem' }}>This is a system-generated AC Health Report by Asian Climate Controls.</p>
                                <p className="mb-0 text-secondary fw-bold x-small" style={{ fontSize: '0.6rem' }}>Audit ID: {data.id || 'Pending'}</p>
                            </div>
                        </div>

                        <div className="modal-footer bg-light no-print border-0 p-2" data-html2canvas-ignore="true">
                            <div className="d-flex gap-2 w-100 justify-content-center">
                                <button className="btn btn-secondary px-3 rounded-pill fw-bold btn-sm" onClick={onClose}>
                                    Close
                                </button>
                                <button className="btn btn-outline-primary px-3 rounded-pill fw-bold btn-sm" onClick={() => {
                                    const element = document.getElementById('report-print');
                                    const opt = {
                                        margin: [3, 3, 3, 3],
                                        filename: `AC_Audit_${data.id || 'Report'}.pdf`,
                                        image: { type: 'jpeg', quality: 0.99 },
                                        html2canvas: { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' },
                                        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: false }
                                    };
                                    html2pdf().set(opt).from(element).save();
                                }}>
                                    <i className="bi bi-file-earmark-pdf-fill me-1"></i>PDF
                                </button>
                                <button className="btn btn-outline-success px-3 rounded-pill fw-bold btn-sm" onClick={() => {
                                    const element = document.getElementById('report-print');
                                    html2canvas(element, { 
                                        scale: 3, 
                                        useCORS: true, 
                                        allowTaint: true,
                                        backgroundColor: '#ffffff',
                                        logging: false
                                    }).then(canvas => {
                                        const link = document.createElement('a');
                                        link.download = `AC_Audit_${data.id || 'Report'}.png`;
                                        link.href = canvas.toDataURL('image/png');
                                        link.click();
                                    });
                                }}>
                                    <i className="bi bi-image-fill me-1"></i>Image
                                </button>
                                <button className="btn btn-primary px-3 rounded-pill fw-bold btn-sm shadow-sm" onClick={() => window.print()}>
                                    <i className="bi bi-printer-fill me-1"></i>Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const filteredAudits = audits.filter(a => {
        const matchesSearch = a.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.mobileNumber.includes(searchTerm);
        const matchesRisk = filterRisk === "All" || a.riskLevel === filterRisk;

        const auditDateObj = new Date(a.auditDate.seconds ? a.auditDate.seconds * 1000 : a.auditDate);
        const matchesDate = !filterDate || auditDateObj.toISOString().split('T')[0] === filterDate;

        return matchesSearch && matchesRisk && matchesDate;
    });

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredAudits.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredAudits.length / rowsPerPage);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold text-primary mb-0">
                        Audit
                    </h3>
                    <p className="text-secondary mb-0">Record and analyze AC health performance</p>
                </div>
                <div className="d-flex gap-2">
                    {view === "list" ? (
                        <>
                            <button className="btn btn-outline-success rounded-pill px-4 shadow-sm" onClick={exportToExcel}>
                                <i className="bi bi-file-earmark-spreadsheet me-2"></i>Export Excel
                            </button>
                            <button className="btn btn-primary rounded-pill px-4 shadow-sm" onClick={() => {
                                setFormData(initialFormState);
                                setIsEditing(false);
                                setEditId(null);
                                setView("form");
                            }}>
                                <i className="bi bi-plus-lg me-2"></i>New Audit
                            </button>
                        </>
                    ) : (
                        <button className="btn btn-outline-primary rounded-pill px-4" onClick={() => {
                            setIsEditing(false);
                            setEditId(null);
                            setView("list");
                        }}>
                            <i className="bi bi-list-task me-2"></i>View History
                        </button>
                    )}

                </div>
            </div>

            {view === "list" ? (
                <div className="card border-0 shadow-sm" style={{ borderRadius: "20px" }}>
                    <div className="card-body p-4">
                        <div className="row g-3 mb-4 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Search</label>
                                <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                    <span className="input-group-text bg-white border-0"><i className="bi bi-search"></i></span>
                                    <input
                                        type="text"
                                        className="form-control border-0"
                                        placeholder="Customer or mobile..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Risk Level</label>
                                <select className="form-select border-0 shadow-sm rounded-3" value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)}>
                                    <option value="All">All Risks</option>
                                    <option value="Low Risk">Low Risk</option>
                                    <option value="Medium Risk">Medium Risk</option>
                                    <option value="High Risk">High Risk</option>
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Audit Date</label>
                                <input type="date" className="form-control border-0 shadow-sm rounded-3" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-light w-100 rounded-3 shadow-sm" onClick={() => {
                                    setSearchTerm("");
                                    setFilterRisk("All");
                                    setFilterDate("");
                                }}>Reset</button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-secondary">Loading audit records...</p>
                            </div>
                        ) : currentRows.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Audit ID</th>
                                            <th>Customer</th>
                                            <th>AC Type</th>
                                            <th>Risk</th>
                                            <th>Score</th>
                                            <th>Date</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRows.map((a) => (
                                            <tr key={a.id}>
                                                <td><small className="text-muted fw-mono">{a.id.slice(0, 8)}...</small></td>
                                                <td>
                                                    <div className="fw-bold">{a.customerName}</div>
                                                    <small className="text-muted">{a.mobileNumber}</small>
                                                </td>
                                                <td>
                                                    <div>{a.ACType}</div>
                                                    <small className="text-muted">{a.serialNumber || a.brand}</small>
                                                </td>
                                                <td>
                                                    <span className={`badge rounded-pill px-3 ${a.riskLevel === 'High Risk' ? 'bg-danger-subtle text-danger' : a.riskLevel === 'Medium Risk' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success'}`}>
                                                        {a.riskLevel}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className={`fw-bold ${a.healthScore < 60 ? 'text-danger' : a.healthScore < 80 ? 'text-warning' : 'text-success'}`}>
                                                        {a.healthScore}
                                                    </div>
                                                </td>
                                                <td>{new Date(a.auditDate.seconds ? a.auditDate.seconds * 1000 : a.auditDate).toLocaleDateString()}</td>
                                                <td className="text-end">
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedAudit(a)} title="View Certificate">
                                                            <i className="bi bi-file-earmark-check"></i>
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-warning" onClick={() => handleEdit(a)} title="Edit Audit">
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(a.id)} title="Delete Audit">
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Pagination UI */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <div className="small text-muted">
                                            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredAudits.length)} of {filteredAudits.length} entries
                                        </div>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                                                </li>
                                                {[...Array(totalPages)].map((_, i) => (
                                                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <i className="bi bi-clipboard-x display-1 text-light"></i>
                                <p className="mt-3 text-secondary">No audit records found.</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        {/* Section 1: Customer Details */}
                        <div className="col-lg-6">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: "20px" }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-4 text-dark border-bottom pb-2">
                                        <i className="bi bi-person-badge text-primary me-2"></i>Customer Details
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label small fw-bold">Customer Name *</label>
                                            <input type="text" className="form-control rounded-3" name="customerName" value={formData.customerName} onChange={handleChange} required placeholder="Full Name" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Mobile Number *</label>
                                            <input type="tel" className="form-control rounded-3" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} required placeholder="10-digit mobile" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Location</label>
                                            <input type="text" className="form-control rounded-3" name="location" value={formData.location} onChange={handleChange} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-bold">Address</label>
                                            <textarea className="form-control rounded-3" name="address" rows="2" value={formData.address} onChange={handleChange} placeholder="Site Address"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: AC Details */}
                        <div className="col-lg-6">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: "20px" }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-4 text-dark border-bottom pb-2">
                                        <i className="bi bi-snow text-primary me-2"></i>AC Unit Details
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">AC Type</label>
                                            <select className="form-select rounded-3" name="ACType" value={formData.ACType} onChange={handleChange}>
                                                <option value="Split">Split AC</option>
                                                <option value="Window">Window AC</option>
                                                <option value="Cassette">Cassette AC</option>
                                                <option value="Ductable">Ductable AC</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Serial Number</label>
                                            <input type="text" className="form-control rounded-3" name="serialNumber" value={formData.serialNumber} onChange={handleChange} placeholder="e.g. SN123456" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Model</label>
                                            <input type="text" className="form-control rounded-3" name="model" value={formData.model} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Tonnage</label>
                                            <input type="text" className="form-control rounded-3" name="tonnage" value={formData.tonnage} onChange={handleChange} placeholder="e.g. 1.5 Ton" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Inst. Year</label>
                                            <input type="number" className="form-control rounded-3" name="installationYear" value={formData.installationYear} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Performance Check */}
                        <div className="col-12">
                            <div className="card border-0 shadow-sm" style={{ borderRadius: "20px" }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-4 text-dark border-bottom pb-2">
                                        <i className="bi bi-activity text-primary me-2"></i>Performance Technical Audit
                                    </h5>
                                    <div className="row g-3">
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Cooling Performance</label>
                                            <select className="form-select rounded-3" name="coolingPerformance" value={formData.coolingPerformance} onChange={handleChange}>
                                                <option value="Good">Good</option>
                                                <option value="Average">Average</option>
                                                <option value="Poor">Poor</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Gas Pressure (PSI)</label>
                                            <input type="number" className="form-control rounded-3" name="gasPressure" value={formData.gasPressure} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Compressor Condition</label>
                                            <select className="form-select rounded-3" name="compressorCondition" value={formData.compressorCondition} onChange={handleChange}>
                                                <option value="Good">Good</option>
                                                <option value="Warning">Warning</option>
                                                <option value="Critical">Critical</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Noise Level</label>
                                            <select className="form-select rounded-3" name="noiseLevel" value={formData.noiseLevel} onChange={handleChange}>
                                                <option value="Low">Low</option>
                                                <option value="Medium">Medium</option>
                                                <option value="High">High</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Power Consumption (Amps)</label>
                                            <input type="number" step="0.1" className="form-control rounded-3" name="powerConsumption" value={formData.powerConsumption} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Grill Temperature (°C)</label>
                                            <input type="number" step="1" className="form-control rounded-3" name="grillTemperature" value={formData.grillTemperature} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Condenser Condition</label>
                                            <input type="text" className="form-control rounded-3" name="condenserCondition" value={formData.condenserCondition} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small fw-bold">Evaporator Condition</label>
                                            <input type="text" className="form-control rounded-3" name="evaporatorCondition" value={formData.evaporatorCondition} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-check mt-4 pt-2">
                                                <input className="form-check-input" type="checkbox" name="waterLeakage" checked={formData.waterLeakage} onChange={handleChange} id="waterLeakage" />
                                                <label className="form-check-label fw-bold small" htmlFor="waterLeakage">Water Leakage Detected</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 4: Maintenance Checklist */}
                        <div className="col-lg-8">
                            <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: "20px" }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-4 text-dark border-bottom pb-2">
                                        <i className="bi bi-tools text-primary me-2"></i>Maintenance Checklist
                                    </h5>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Filter Status</label>
                                            <select className="form-select rounded-3" name="filterStatus" value={formData.filterStatus} onChange={handleChange}>
                                                <option value="Clean">Clean</option>
                                                <option value="Dirty">Dirty</option>
                                                <option value="Replace Required">Replace Required</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mt-4 pt-1">
                                                <div className="form-check form-check-inline">
                                                    <input className="form-check-input" type="checkbox" name="coilCleaningRequired" checked={formData.coilCleaningRequired} onChange={handleChange} id="coilCleaning" />
                                                    <label className="form-check-label small fw-bold" htmlFor="coilCleaning">Coil Cleaning Req.</label>
                                                </div>
                                                <div className="form-check form-check-inline ms-3">
                                                    <input className="form-check-input" type="checkbox" name="gasRefillingRequired" checked={formData.gasRefillingRequired} onChange={handleChange} id="gasRefill" />
                                                    <label className="form-check-label small fw-bold" htmlFor="gasRefill">Gas Refill Req.</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label small fw-bold">Final Recommended Actions</label>
                                            <textarea className="form-control rounded-3" name="recommendedActions" rows="3" value={formData.recommendedActions} onChange={handleChange} placeholder="What should the customer do next?"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Health Score Panel */}
                        <div className="col-lg-4">
                            <div className="card h-100 border-0 shadow-sm overflow-hidden" style={{ borderRadius: "20px" }}>
                                <div className={`p-4 text-center ${healthScore < 60 ? 'bg-danger text-white' : healthScore < 80 ? 'bg-warning text-dark' : 'bg-success text-white'}`}>
                                    <h6 className="text-uppercase fw-bold mb-1" style={{ opacity: 0.8 }}>Live Health Score</h6>
                                    <h1 className="display-3 fw-bold mb-0">{healthScore}</h1>
                                    <p className="fw-bold mb-0">{riskLevel}</p>
                                </div>
                                <div className="card-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Estimated Service Cost</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">₹</span>
                                            <input type="number" className="form-control bg-light border-start-0 fw-bold" name="estimatedServiceCost" value={formData.estimatedServiceCost} onChange={handleChange} placeholder="0.00" />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Next Service Due</label>
                                        <input type="date" className="form-control bg-light" name="nextServiceDue" value={formData.nextServiceDue} onChange={handleChange} />
                                    </div>
                                    <div>
                                        <label className="form-label small fw-bold text-muted">Audited By (Technician Name)</label>
                                        <select
                                            className="form-select bg-light"
                                            name="technicianName"
                                            value={formData.technicianName}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Technician</option>
                                            {technicians.map((tech, idx) => (
                                                <option key={idx} value={tech}>{tech}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="d-flex gap-3 mt-5">
                        <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>Saving Audit...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-cloud-arrow-up me-2"></i>Submit Audit Report
                                </>
                            )}
                        </button>
                        <button type="button" className="btn btn-info btn-lg rounded-pill px-5 text-white shadow-sm" onClick={() => setShowSummary(true)}>
                            <i className="bi bi-file-earmark-text me-2"></i>Preview Summary
                        </button>
                    </div>
                </form>
            )}

            {showSummary && <SummaryPreview data={{ ...formData, healthScore, riskLevel }} onClose={() => setShowSummary(false)} />}
            {selectedAudit && <SummaryPreview data={selectedAudit} onClose={() => setSelectedAudit(null)} />}
        </div>
    );
};

export default ACAudit;
